import {useLazyQuery} from '@apollo/client/react';
import {castArray} from '@coolcolduk/util';
import {Question, QuestionStatus} from '@cp/model';
import {LIST_QUESTIONS} from '../sharedQueries';

export interface ListQuestionsVariables {
  moduleId: string;
  topics?: string[];
  difficulty?: number;
  status?: QuestionStatus;
  limit?: number;
  offset?: number;
}

export interface ListQuestionsSingleTopicVariables extends Omit<ListQuestionsVariables, 'topics'> {
  topic?: string;
}

function mapSingleToMultiTopicVariables(
  variables: ListQuestionsSingleTopicVariables | ListQuestionsVariables,
): ListQuestionsVariables {
  const topics =
    'topics' in variables ? variables.topics : castArray((variables as ListQuestionsSingleTopicVariables).topic || []);
  return {
    ...variables,
    topics,
  };
}

export function useListQuestions() {
  const [fetchQuestionsGql, {data, loading, error, refetch}] = useLazyQuery<
    {listQuestions: Question[]},
    ListQuestionsVariables
  >(LIST_QUESTIONS, {
    fetchPolicy: 'cache-first', // Check cache first, only fetch from network if not in cache
    nextFetchPolicy: 'cache-only', // Use cache only for subsequent fetches with same parameters (no network request)
  });

  const fetchQuestions = (variables: ListQuestionsSingleTopicVariables | ListQuestionsVariables) => {
    // Filter out undefined values to ensure consistent cache keys
    const queryVariables = mapSingleToMultiTopicVariables(variables);

    return fetchQuestionsGql({
      variables: queryVariables,
    });
  };

  return {
    questions: data?.listQuestions || [],
    loading,
    error,
    fetchQuestions,
    refetch,
  };
}
